import React, { useState } from 'react';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from './ui/dialog';
import { Thermometer, Cloud, BarChart3, Bell, Settings, Loader2, RefreshCw, LineChart, Info, Monitor, ArrowLeft } from 'lucide-react';
import { getJwtToken } from '../services/auth';
import { GraphModal } from './GraphModal';
import { Tooltip } from './ui/tooltip';

export interface TemperatureRanges {
    normal: {
        min: number;
        max: number;
    };
    warning: {
        min: number;
        max: number;
    };
}

export interface PressureRanges {
    normal: {
        min: number;
        max: number;
    };
    warning: {
        min: number;
        max: number;
    };
}

interface OperationsFiltersProps {
    showTemperatureZones: boolean;
    showPressureSystems: boolean;
    showProcessData: boolean;
    showAlarms: boolean;
    onTemperatureZonesChange: (checked: boolean) => void;
    onPressureSystemsChange: (checked: boolean) => void;
    onProcessDataChange: (checked: boolean) => void;
    onAlarmsChange: (checked: boolean) => void;
    temperatureRanges?: TemperatureRanges;
    onTemperatureRangesChange?: (ranges: TemperatureRanges) => void;
    edgeUid?: string;
    apiMinimumTemperature?: number | null;
    apiMaximumTemperature?: number | null;
    apiMinimumPressure?: number | null;
    apiMaximumPressure?: number | null;
    apiUnderMinimumTemperature?: number | null;
    apiOverMaximumTemperature?: number | null;
    apiUnderMinimumPressure?: number | null;
    apiOverMaximumPressure?: number | null;
    refreshInterval?: number;
    onRefreshIntervalChange?: (interval: number) => void;
    onRefreshIntervalSave?: (refreshIntervalSeconds: number) => void;
    onRefreshData?: () => void | Promise<void>;
    coatingType?: string;
    alarmDetails?: any[];
    raptorConfig?: {
        fbe: { alarmLow: number; warningLow: number; warningHigh: number; alarmHigh: number };
        cmpp: { alarmLow: number; warningLow: number; warningHigh: number; alarmHigh: number };
    };
    onRaptorConfigChange?: (config: NonNullable<OperationsFiltersProps['raptorConfig']>) => void;
}

export const OperationsFilters: React.FC<OperationsFiltersProps> = ({
    showTemperatureZones,
    showPressureSystems,
    showProcessData,
    showAlarms,
    onTemperatureZonesChange,
    onPressureSystemsChange,
    onProcessDataChange,
    onAlarmsChange,
    temperatureRanges,
    onTemperatureRangesChange,
    edgeUid,
    apiMinimumTemperature,
    apiMaximumTemperature,
    apiMinimumPressure,
    apiMaximumPressure,
    apiUnderMinimumTemperature,
    apiOverMaximumTemperature,
    apiUnderMinimumPressure,
    apiOverMaximumPressure,
    refreshInterval = 5,
    onRefreshIntervalChange,
    onRefreshIntervalSave,
    onRefreshData,
    coatingType,
    alarmDetails,
    raptorConfig,
    onRaptorConfigChange,
}) => {
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
    const [isSystemDetailsOpen, setIsSystemDetailsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const normalizedCoatingType = (coatingType || '').toString().toLowerCase();
    const isRaptor = normalizedCoatingType === 'raptor';

    const temperatureLabel = isRaptor ? 'FBE' : 'Temperature Zones';
    const pressureLabel = isRaptor ? 'CMPP' : 'Pressure Systems';
    const processLabel = isRaptor ? 'Air Control' : 'Process Data';

    const latestAlarmDetails = Array.isArray(alarmDetails) && alarmDetails.length > 0 ? alarmDetails[0] : null;

    const KeyValueTable = ({ data }: { data?: Record<string, any> | null }) => {
        if (!data || typeof data !== 'object') return null;
        const entries = Object.entries(data);
        if (entries.length === 0) return null;
        return (
            <div className="overflow-auto rounded border border-gray-200">
                <table className="min-w-full border-collapse w-full">
                    <thead className="bg-gray-50 sticky top-0">
                        <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase border border-gray-300 w-[60%]">Name</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase border border-gray-300 w-[40%]">Value</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {entries.map(([k, v]) => (
                            <tr key={k}>
                                <td className="px-3 py-2 text-sm text-gray-800 border border-gray-300 w-[60%]">{k}</td>
                                <td className="px-3 py-2 text-sm text-right font-mono text-gray-900 border border-gray-300 w-[40%]">
                                    {v === null || v === undefined ? 'N/A' : String(v)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const [localRaptorConfig, setLocalRaptorConfig] = useState(() => {
        return (
            raptorConfig || {
                fbe: { alarmLow: -0.5, warningLow: -0.1, warningHigh: 0.1, alarmHigh: 0.5 },
                cmpp: { alarmLow: -0.5, warningLow: -0.1, warningHigh: 0.1, alarmHigh: 0.5 },
            }
        );
    });
    
    // Initialize local ranges with API values if available, otherwise use temperatureRanges or defaults
    const [localRanges, setLocalRanges] = useState<TemperatureRanges>(() => {
        if (apiMinimumTemperature !== null && apiMinimumTemperature !== undefined && 
            apiMaximumTemperature !== null && apiMaximumTemperature !== undefined) {
            return {
                normal: { min: apiMinimumTemperature, max: apiMaximumTemperature },
                warning: { min: 0, max: apiMinimumTemperature - 1 },
            };
        }
        return temperatureRanges || {
            normal: { min: 245, max: 255 },
            warning: { min: 0, max: 244 },
        };
    });

    // Initialize local temperature under_minimum and over_maximum values (prefer API values)
    const [localTemperatureUnderMinimum, setLocalTemperatureUnderMinimum] = useState<number>(
        () => (apiUnderMinimumTemperature ?? 0)
    );
    const [localTemperatureOverMaximum, setLocalTemperatureOverMaximum] = useState<number>(
        () => (apiOverMaximumTemperature ?? 0)
    );

    // Initialize local pressure ranges with API values if available, otherwise use defaults
    const [localPressureRanges, setLocalPressureRanges] = useState<PressureRanges>(() => {
        if (apiMinimumPressure !== null && apiMinimumPressure !== undefined && 
            apiMaximumPressure !== null && apiMaximumPressure !== undefined) {
            return {
                normal: { min: apiMinimumPressure, max: apiMaximumPressure },
                warning: { min: 0, max: apiMinimumPressure - 1 },
            };
        }
        return {
            normal: { min: 80, max: 100 },
            warning: { min: 0, max: 79 },
        };
    });

    // Initialize local pressure under_minimum and over_maximum values (prefer API values)
    const [localPressureUnderMinimum, setLocalPressureUnderMinimum] = useState<number>(
        () => (apiUnderMinimumPressure ?? 0)
    );
    const [localPressureOverMaximum, setLocalPressureOverMaximum] = useState<number>(
        () => (apiOverMaximumPressure ?? 0)
    );

    // Helper function to split minutes into whole minutes and seconds
    const splitMinutesToMinutesAndSeconds = (minutes: number) => {
        const wholeMinutes = Math.floor(minutes);
        const decimalPart = minutes - wholeMinutes;
        const seconds = Math.round(decimalPart * 60);
        return { wholeMinutes, seconds };
    };

    // Initialize local refresh interval (split into minutes and seconds)
    const initialInterval = splitMinutesToMinutesAndSeconds(refreshInterval);
    const [localRefreshIntervalMinutes, setLocalRefreshIntervalMinutes] = useState<number>(initialInterval.wholeMinutes);
    const [localRefreshIntervalSeconds, setLocalRefreshIntervalSeconds] = useState<number>(initialInterval.seconds);

    const handleSaveRanges = async () => {
        setIsSaving(true);
        setSaveError(null);

        try {
            // Raptor devices: save FBE/CMPP thresholds as device_threshold_config JSON + refresh interval
            if (isRaptor) {
                if (!edgeUid) {
                    setSaveError('Edge UID is required');
                    return;
                }

                const fbe = localRaptorConfig?.fbe;
                const cmpp = localRaptorConfig?.cmpp;

                const isValidRanges = (p?: { alarmLow: number; warningLow: number; warningHigh: number; alarmHigh: number }) =>
                    p &&
                    [p.alarmLow, p.warningLow, p.warningHigh, p.alarmHigh].every((x) => typeof x === 'number' && Number.isFinite(x)) &&
                    p.alarmLow <= p.warningLow &&
                    p.warningLow <= p.warningHigh &&
                    p.warningHigh <= p.alarmHigh;

                if (!isValidRanges(fbe) || !isValidRanges(cmpp)) {
                    setSaveError('Please enter valid thresholds (Alarm Low ≤ Warning Low ≤ Warning High ≤ Alarm High).');
                    return;
                }

                // Convert minutes to seconds and add with seconds value
                const totalSeconds = (localRefreshIntervalMinutes * 60) + localRefreshIntervalSeconds;
                const apiUrl = process.env.REACT_APP_LIMITS_API_BASE_URL || 'http://localhost:9000';
                const endpoint = `${apiUrl}/coating-data/temperature-configuration`;
                const authToken = await getJwtToken();

                const payload = {
                    edge_uid: edgeUid,
                    refresh_interval_seconds: totalSeconds,
                    device_threshold_config: localRaptorConfig,
                };

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': authToken || '',
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: 'Failed to save configuration' }));
                    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
                }

                // Update parent component state on success
                if (onRaptorConfigChange) {
                    onRaptorConfigChange(localRaptorConfig);
                }
                if (onRefreshIntervalSave) {
                    onRefreshIntervalSave(totalSeconds);
                }
                const totalMinutes = localRefreshIntervalMinutes + (localRefreshIntervalSeconds / 60);
                if (onRefreshIntervalChange) {
                    onRefreshIntervalChange(totalMinutes);
                }

                setIsConfigModalOpen(false);
                return;
            }

            if (!edgeUid) {
                setSaveError('Edge UID is required');
                return;
            }

            const apiUrl = process.env.REACT_APP_LIMITS_API_BASE_URL || 'http://localhost:9000';
            const endpoint = `${apiUrl}/coating-data/temperature-configuration`;
            const authToken = await getJwtToken();

            // Convert minutes to seconds and add with seconds value
            const totalSeconds = (localRefreshIntervalMinutes * 60) + localRefreshIntervalSeconds;

            const payload = {
                edge_uid: edgeUid,
                minimum_temperature: localRanges.normal.min,
                maximum_temperature: localRanges.normal.max,
                under_minimum_temperature: localTemperatureUnderMinimum,
                over_maximum_temperature: localTemperatureOverMaximum,
                minimum_pressure: localPressureRanges.normal.min,
                maximum_pressure: localPressureRanges.normal.max,
                under_minimum_pressure: localPressureUnderMinimum,
                over_maximum_pressure: localPressureOverMaximum,
                refresh_interval_seconds: totalSeconds,
            };

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authToken || '',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to save configuration' }));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            // Update parent component state on success
            if (onTemperatureRangesChange) {
                onTemperatureRangesChange(localRanges);
            }
            // Update polling interval with the new refresh_interval_seconds value
            // This will also update the parent's refreshInterval state
            if (onRefreshIntervalSave) {
                onRefreshIntervalSave(totalSeconds);
            }
            // Also update the parent's refreshInterval state for UI consistency
            // Calculate total minutes for parent component (for display purposes)
            const totalMinutes = localRefreshIntervalMinutes + (localRefreshIntervalSeconds / 60);
            if (onRefreshIntervalChange) {
                onRefreshIntervalChange(totalMinutes);
            }
            setIsConfigModalOpen(false);
        } catch (error: any) {
            console.error('Error saving configuration:', error);
            setSaveError(error.message || 'Failed to save configuration');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        // Reset to API values if available, otherwise use temperatureRanges or defaults
        if (apiMinimumTemperature !== null && apiMinimumTemperature !== undefined && 
            apiMaximumTemperature !== null && apiMaximumTemperature !== undefined) {
            setLocalRanges({
                normal: { min: apiMinimumTemperature, max: apiMaximumTemperature },
                warning: { min: 0, max: apiMinimumTemperature - 1 },
            });
            setLocalTemperatureUnderMinimum(apiUnderMinimumTemperature ?? 0);
            setLocalTemperatureOverMaximum(apiOverMaximumTemperature ?? 0);
        } else {
            setLocalRanges(
                temperatureRanges || {
                    normal: { min: 245, max: 255 },
                    warning: { min: 0, max: 244 },
                }
            );
            setLocalTemperatureUnderMinimum(0);
            setLocalTemperatureOverMaximum(0);
        }
        // Reset pressure ranges to API values if available, otherwise use defaults
        if (apiMinimumPressure !== null && apiMinimumPressure !== undefined && 
            apiMaximumPressure !== null && apiMaximumPressure !== undefined) {
            setLocalPressureRanges({
                normal: { min: apiMinimumPressure, max: apiMaximumPressure },
                warning: { min: 0, max: apiMinimumPressure - 1 },
            });
            setLocalPressureUnderMinimum(apiUnderMinimumPressure ?? 0);
            setLocalPressureOverMaximum(apiOverMaximumPressure ?? 0);
        } else {
            setLocalPressureRanges({
                normal: { min: 80, max: 100 },
                warning: { min: 0, max: 79 },
            });
            setLocalPressureUnderMinimum(0);
            setLocalPressureOverMaximum(0);
        }
        // Reset refresh interval to prop value (split into minutes and seconds)
        const resetInterval = splitMinutesToMinutesAndSeconds(refreshInterval);
        setLocalRefreshIntervalMinutes(resetInterval.wholeMinutes);
        setLocalRefreshIntervalSeconds(resetInterval.seconds);
        if (raptorConfig) {
            setLocalRaptorConfig(raptorConfig);
        }
        setIsConfigModalOpen(false);
    };

    // Update local ranges when API values or temperatureRanges prop changes
    React.useEffect(() => {
        if (apiMinimumTemperature !== null && apiMinimumTemperature !== undefined && 
            apiMaximumTemperature !== null && apiMaximumTemperature !== undefined) {
            setLocalRanges({
                normal: { min: apiMinimumTemperature, max: apiMaximumTemperature },
                warning: { min: 0, max: apiMinimumTemperature - 1 },
            });
        } else if (temperatureRanges) {
            setLocalRanges(temperatureRanges);
        }
    }, [apiMinimumTemperature, apiMaximumTemperature, temperatureRanges]);

    // Update local temperature alarm thresholds when API values change
    React.useEffect(() => {
        if (apiUnderMinimumTemperature !== null && apiUnderMinimumTemperature !== undefined) {
            setLocalTemperatureUnderMinimum(apiUnderMinimumTemperature);
        }
        if (apiOverMaximumTemperature !== null && apiOverMaximumTemperature !== undefined) {
            setLocalTemperatureOverMaximum(apiOverMaximumTemperature);
        }
    }, [apiUnderMinimumTemperature, apiOverMaximumTemperature]);

    // Update local pressure ranges when API values change
    React.useEffect(() => {
        if (apiMinimumPressure !== null && apiMinimumPressure !== undefined && 
            apiMaximumPressure !== null && apiMaximumPressure !== undefined) {
            setLocalPressureRanges({
                normal: { min: apiMinimumPressure, max: apiMaximumPressure },
                warning: { min: 0, max: apiMinimumPressure - 1 },
            });
        }
    }, [apiMinimumPressure, apiMaximumPressure]);

    // Update local pressure alarm thresholds when API values change
    React.useEffect(() => {
        if (apiUnderMinimumPressure !== null && apiUnderMinimumPressure !== undefined) {
            setLocalPressureUnderMinimum(apiUnderMinimumPressure);
        }
        if (apiOverMaximumPressure !== null && apiOverMaximumPressure !== undefined) {
            setLocalPressureOverMaximum(apiOverMaximumPressure);
        }
    }, [apiUnderMinimumPressure, apiOverMaximumPressure]);

    // Update local refresh interval when prop changes (split into minutes and seconds)
    React.useEffect(() => {
        const updatedInterval = splitMinutesToMinutesAndSeconds(refreshInterval);
        setLocalRefreshIntervalMinutes(updatedInterval.wholeMinutes);
        setLocalRefreshIntervalSeconds(updatedInterval.seconds);
    }, [refreshInterval]);

    React.useEffect(() => {
        if (raptorConfig) {
            setLocalRaptorConfig(raptorConfig);
        }
    }, [raptorConfig]);

    return (
        <>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6 w-full">
                <div className="flex flex-wrap items-center gap-4 md:gap-6">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="temperature-zones"
                            checked={showTemperatureZones}
                            onCheckedChange={onTemperatureZonesChange}
                        />
                        <Label htmlFor="temperature-zones" className="flex items-center gap-2 cursor-pointer">
                            <Thermometer className="h-5 w-5 text-pink-500" />
                            <span className="text-sm text-gray-700">{temperatureLabel}</span>
                        </Label>
                    </div>

                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="pressure-systems"
                            checked={showPressureSystems}
                            onCheckedChange={onPressureSystemsChange}
                        />
                        <Label htmlFor="pressure-systems" className="flex items-center gap-2 cursor-pointer">
                            <Cloud className="h-5 w-5 text-purple-400" />
                            <span className="text-sm text-gray-700">{pressureLabel}</span>
                        </Label>
                    </div>

                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="process-data"
                            checked={showProcessData}
                            onCheckedChange={onProcessDataChange}
                        />
                        <Label htmlFor="process-data" className="flex items-center gap-2 cursor-pointer">
                            <BarChart3 className="h-5 w-5 text-blue-500" />
                            <span className="text-sm text-gray-700">{processLabel}</span>
                        </Label>
                    </div>

                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="alarms"
                            checked={showAlarms}
                            onCheckedChange={onAlarmsChange}
                        />
                        <Label htmlFor="alarms" className="flex items-center gap-2 cursor-pointer">
                            <Bell className="h-5 w-5 text-red-500" />
                            <span className="text-sm text-gray-700">Alarms</span>
                        </Label>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                            if (!onRefreshData || isRefreshing) return;
                            setIsRefreshing(true);
                            try {
                                await Promise.resolve(onRefreshData());
                            } finally {
                                setIsRefreshing(false);
                            }
                        }}
                        className="flex items-center gap-2"
                        title="Refresh temperature data"
                    >
                        <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                        <span className="hidden sm:inline">Refresh</span>
                    </Button>
                    {isRaptor && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsSystemDetailsOpen(true)}
                            className="flex items-center gap-2"
                            title="View system details"
                        >
                            <Monitor className="h-4 w-4" />
                            <span className="hidden sm:inline">System Details</span>
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsGraphModalOpen(true)}
                        className="flex items-center gap-2"
                        title="View graphs"
                    >
                        <LineChart className="h-4 w-4" />
                        <span className="hidden sm:inline">Graph</span>
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsConfigModalOpen(true)}
                        className="flex items-center gap-2"
                    >
                        <Settings className="h-4 w-4" />
                        <span className="hidden sm:inline">Configure</span>
                    </Button>
                </div>
            </div>

            <Dialog open={isConfigModalOpen} onOpenChange={setIsConfigModalOpen}>
                <DialogContent className="sm:max-w-[1000px]">
                    <DialogHeader>
                        <DialogTitle>Zone Configuration</DialogTitle>
                        <DialogDescription>
                            {isRaptor
                                ? 'Set FBE and CMPP deviation thresholds and the refresh interval.'
                                : 'Set the temperature and pressure ranges for normal and warning zones.'}
                            {isRaptor && raptorConfig && (
                                <>
                                    {raptorConfig.fbe && (
                                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                                            <strong>Current FBE Deviation Range:</strong> Alarm Low: {raptorConfig.fbe.alarmLow}, Warning Low: {raptorConfig.fbe.warningLow}, Warning High: {raptorConfig.fbe.warningHigh}, Alarm High: {raptorConfig.fbe.alarmHigh}
                                        </div>
                                    )}
                                    {raptorConfig.cmpp && (
                                        <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-xs text-purple-700">
                                            <strong>Current CMPP Deviation Range:</strong> Alarm Low: {raptorConfig.cmpp.alarmLow}, Warning Low: {raptorConfig.cmpp.warningLow}, Warning High: {raptorConfig.cmpp.warningHigh}, Alarm High: {raptorConfig.cmpp.alarmHigh}
                                        </div>
                                    )}
                                </>
                            )}
                            {!isRaptor && apiMinimumTemperature !== null && apiMinimumTemperature !== undefined && 
                             apiMaximumTemperature !== null && apiMaximumTemperature !== undefined && (
                                <div className="mt-2 p-2 bg-blue-50 border rounded text-xs text-blue-700">
                                    <strong>Current Temperature Range:</strong> Alarm Low : {apiUnderMinimumTemperature} °C, Warning Low : {apiMinimumTemperature} °C, Warning High : {apiMaximumTemperature} °C, Alarm High : {apiOverMaximumTemperature} °C
                                </div>
                            )}
                            {!isRaptor && apiMinimumPressure !== null && apiMinimumPressure !== undefined && 
                             apiMaximumPressure !== null && apiMaximumPressure !== undefined && (
                                <div className="mt-2 p-2 bg-purple-50 border rounded text-xs text-purple-700">
                                    <strong>Current Pressure Range:</strong> Alarm Low : {apiUnderMinimumPressure} Bar, Warning Low : {apiMinimumPressure} Bar, Warning High : {apiMaximumPressure} Bar, Alarm High : {apiOverMaximumPressure} Bar
                                </div>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        {/* Temperature Zone Range (non-raptor only) */}
                        {!isRaptor && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <Label className="text-base font-semibold text-green-700 flex items-center gap-2">
                                    Temperature Zone Range (°C)
                                </Label>
                                <Tooltip
                                    placement="right"
                                    contentClassName="w-[720px] max-w-[calc(100vw-2rem)]"
                                    content={
                                        <div className="text-xs leading-relaxed">
                                            <div className="font-semibold mb-2">Temperature deviation classification</div>
                                            <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1">
                                                <div className="font-semibold">Normal</div>
                                                <div className="break-words">Warning Low ≤ deviation ≤ Warning High</div>

                                                <div className="font-semibold">Warning</div>
                                                <div className="break-words">Alarm Low ≤ deviation &lt; Warning Low, or Warning High &lt; deviation ≤ Alarm High</div>

                                                <div className="font-semibold">Danger</div>
                                                <div className="break-words">deviation &lt; Alarm Low, or deviation &gt; Alarm High</div>
                                            </div>
                                        </div>
                                    }
                                >
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                                        aria-label="Temperature zone rules"
                                    >
                                        <Info className="h-4 w-4" />
                                    </Button>
                                </Tooltip>
                            </div>
                            <div className="grid grid-cols-4 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="under-minimum-temp" className="text-sm text-gray-600">
                                        Alarm Low Threshold (°C)
                                    </Label>
                                    <Input
                                        id="under-minimum-temp"
                                        type="number"
                                        value={localTemperatureUnderMinimum}
                                        onChange={(e) =>
                                            setLocalTemperatureUnderMinimum(parseFloat(e.target.value) || 0)
                                        }
                                        placeholder="Alarm Low"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="normal-min" className="text-sm text-gray-600">
                                        Warning Low Threshold (°C)
                                    </Label>
                                    <Input
                                        id="normal-min"
                                        type="number"
                                        value={localRanges.normal.min}
                                        onChange={(e) =>
                                            setLocalRanges({
                                                ...localRanges,
                                                normal: {
                                                    ...localRanges.normal,
                                                    min: parseFloat(e.target.value) || 0,
                                                },
                                            })
                                        }
                                        placeholder="Warning Low"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="normal-max" className="text-sm text-gray-600">
                                        Warning High Threshold (°C)
                                    </Label>
                                    <Input
                                        id="normal-max"
                                        type="number"
                                        value={localRanges.normal.max}
                                        onChange={(e) =>
                                            setLocalRanges({
                                                ...localRanges,
                                                normal: {
                                                    ...localRanges.normal,
                                                    max: parseFloat(e.target.value) || 0,
                                                },
                                            })
                                        }
                                        placeholder="Warning High"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="over-maximum-temp" className="text-sm text-gray-600">
                                        Alarm High Threshold (°C)
                                    </Label>
                                    <Input
                                        id="over-maximum-temp"
                                        type="number"
                                        value={localTemperatureOverMaximum}
                                        onChange={(e) =>
                                            setLocalTemperatureOverMaximum(parseFloat(e.target.value) || 0)
                                        }
                                        placeholder="Alarm High"
                                    />
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Pressure Zone Range (non-raptor only) */}
                        {!isRaptor && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <Label className="text-base font-semibold text-purple-700 flex items-center gap-2">
                                    Pressure Zone Range (Bar)
                                </Label>
                                <Tooltip
                                    placement="right"
                                    contentClassName="w-[720px] max-w-[calc(100vw-2rem)]"
                                    content={
                                        <div className="text-xs leading-relaxed">
                                            <div className="font-semibold mb-2">Pressure deviation classification</div>
                                            <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1">
                                                <div className="font-semibold">Normal</div>
                                                <div className="break-words">Warning Low ≤ deviation ≤ Warning High</div>

                                                <div className="font-semibold">Warning</div>
                                                <div className="break-words">Alarm Low ≤ deviation &lt; Warning Low, or Warning High &lt; deviation ≤ Alarm High</div>

                                                <div className="font-semibold">Danger</div>
                                                <div className="break-words">deviation &lt; Alarm Low, or deviation &gt; Alarm High</div>
                                            </div>
                                        </div>
                                    }
                                >
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                                        aria-label="Pressure zone rules"
                                    >
                                        <Info className="h-4 w-4" />
                                    </Button>
                                </Tooltip>
                            </div>
                            <div className="grid grid-cols-4 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="under-minimum-pressure" className="text-sm text-gray-600">
                                        Alarm Low Threshold (Bar)
                                    </Label>
                                    <Input
                                        id="under-minimum-pressure"
                                        type="number"
                                        value={localPressureUnderMinimum}
                                        onChange={(e) =>
                                            setLocalPressureUnderMinimum(parseFloat(e.target.value) || 0)
                                        }
                                        placeholder="Alarm Low"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="pressure-min" className="text-sm text-gray-600">
                                        Warning Low Threshold (Bar)
                                    </Label>
                                    <Input
                                        id="pressure-min"
                                        type="number"
                                        value={localPressureRanges.normal.min}
                                        onChange={(e) =>
                                            setLocalPressureRanges({
                                                ...localPressureRanges,
                                                normal: {
                                                    ...localPressureRanges.normal,
                                                    min: parseFloat(e.target.value) || 0,
                                                },
                                            })
                                        }
                                        placeholder="Warning Low"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="pressure-max" className="text-sm text-gray-600">
                                        Warning High Threshold (Bar)
                                    </Label>
                                    <Input
                                        id="pressure-max"
                                        type="number"
                                        value={localPressureRanges.normal.max}
                                        onChange={(e) =>
                                            setLocalPressureRanges({
                                                ...localPressureRanges,
                                                normal: {
                                                    ...localPressureRanges.normal,
                                                    max: parseFloat(e.target.value) || 0,
                                                },
                                            })
                                        }
                                        placeholder="Warning High"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="over-maximum-pressure" className="text-sm text-gray-600">
                                        Alarm High Threshold (Bar)
                                    </Label>
                                    <Input
                                        id="over-maximum-pressure"
                                        type="number"
                                        value={localPressureOverMaximum}
                                        onChange={(e) =>
                                            setLocalPressureOverMaximum(parseFloat(e.target.value) || 0)
                                        }
                                        placeholder="Alarm High"
                                    />
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Raptor Configuration (FBE + CMPP) */}
                        {isRaptor && (
                            <div className="space-y-6">
                                <div className="space-y-3">
                                    <Label className="text-base font-semibold text-green-700 flex items-center gap-2">
                                        FBE Deviation Thresholds
                                    </Label>
                                    <div className="grid grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-sm text-gray-600">Alarm Low Threshold</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={localRaptorConfig?.fbe?.alarmLow ?? 0}
                                                onChange={(e) => {
                                                    const v = parseFloat(e.target.value) || 0;
                                                    setLocalRaptorConfig((prev: any) => ({
                                                        ...prev,
                                                        fbe: { ...(prev?.fbe || {}), alarmLow: v },
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-gray-600">Warning Low Threshold</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={localRaptorConfig?.fbe?.warningLow ?? 0}
                                                onChange={(e) => {
                                                    const v = parseFloat(e.target.value) || 0;
                                                    setLocalRaptorConfig((prev: any) => ({
                                                        ...prev,
                                                        fbe: { ...(prev?.fbe || {}), warningLow: v },
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-gray-600">Warning High Threshold</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={localRaptorConfig?.fbe?.warningHigh ?? 0}
                                                onChange={(e) => {
                                                    const v = parseFloat(e.target.value) || 0;
                                                    setLocalRaptorConfig((prev: any) => ({
                                                        ...prev,
                                                        fbe: { ...(prev?.fbe || {}), warningHigh: v },
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-gray-600">Alarm High Threshold</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={localRaptorConfig?.fbe?.alarmHigh ?? 0}
                                                onChange={(e) => {
                                                    const v = parseFloat(e.target.value) || 0;
                                                    setLocalRaptorConfig((prev: any) => ({
                                                        ...prev,
                                                        fbe: { ...(prev?.fbe || {}), alarmHigh: v },
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <Label className="text-base font-semibold text-purple-700 flex items-center gap-2">
                                        CMPP Deviation Thresholds
                                    </Label>
                                    <div className="grid grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-sm text-gray-600">Alarm Low Threshold</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={localRaptorConfig?.cmpp?.alarmLow ?? 0}
                                                onChange={(e) => {
                                                    const v = parseFloat(e.target.value) || 0;
                                                    setLocalRaptorConfig((prev: any) => ({
                                                        ...prev,
                                                        cmpp: { ...(prev?.cmpp || {}), alarmLow: v },
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-gray-600">Warning Low Threshold</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={localRaptorConfig?.cmpp?.warningLow ?? 0}
                                                onChange={(e) => {
                                                    const v = parseFloat(e.target.value) || 0;
                                                    setLocalRaptorConfig((prev: any) => ({
                                                        ...prev,
                                                        cmpp: { ...(prev?.cmpp || {}), warningLow: v },
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-gray-600">Warning High Threshold</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={localRaptorConfig?.cmpp?.warningHigh ?? 0}
                                                onChange={(e) => {
                                                    const v = parseFloat(e.target.value) || 0;
                                                    setLocalRaptorConfig((prev: any) => ({
                                                        ...prev,
                                                        cmpp: { ...(prev?.cmpp || {}), warningHigh: v },
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-gray-600">Alarm High Threshold</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={localRaptorConfig?.cmpp?.alarmHigh ?? 0}
                                                onChange={(e) => {
                                                    const v = parseFloat(e.target.value) || 0;
                                                    setLocalRaptorConfig((prev: any) => ({
                                                        ...prev,
                                                        cmpp: { ...(prev?.cmpp || {}), alarmHigh: v },
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Refresh Interval */}
                        <div className="space-y-3">
                            <Label className="text-base font-semibold text-blue-700 flex items-center gap-2">
                                Refresh Interval
                            </Label>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="refresh-interval-minutes" className="text-sm text-gray-600">
                                        Minutes
                                    </Label>
                                    <Input
                                        id="refresh-interval-minutes"
                                        type="number"
                                        min="0"
                                        value={localRefreshIntervalMinutes}
                                        onChange={(e) =>
                                            setLocalRefreshIntervalMinutes(parseInt(e.target.value) || 0)
                                        }
                                        placeholder="0"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="refresh-interval-seconds" className="text-sm text-gray-600">
                                        Seconds
                                    </Label>
                                    <Input
                                        id="refresh-interval-seconds"
                                        type="number"
                                        min="0"
                                        max="59"
                                        value={localRefreshIntervalSeconds}
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value) || 0;
                                            setLocalRefreshIntervalSeconds(Math.min(Math.max(value, 0), 59));
                                        }}
                                        placeholder="0"
                                    />
                                </div>
                            </div>
                            <p className="text-xs text-gray-500">
                                Total: {(localRefreshIntervalMinutes * 60) + localRefreshIntervalSeconds} seconds
                            </p>
                        </div>

                        {/* Error Message */}
                        {saveError && (
                            <div className="p-3 bg-red-50 border rounded-lg">
                                <p className="text-sm text-red-700">{saveError}</p>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveRanges} disabled={isSaving || (!isRaptor && !edgeUid)}>
                            {isSaving ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Saving...
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Graph Modal */}
            <GraphModal
                isOpen={isGraphModalOpen}
                onClose={() => setIsGraphModalOpen(false)}
                edgeUid={edgeUid}
                refreshIntervalSeconds={refreshInterval ? Math.round(refreshInterval * 60) : undefined}
                coatingType={coatingType}
            />

            {/* System Details (Raptor) */}
            <Dialog open={isSystemDetailsOpen} onOpenChange={setIsSystemDetailsOpen}>
                <DialogContent className="sm:max-w-[1000px]">
                    <DialogHeader className="relative px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0 flex flex-row items-start justify-between">
                        {/* Back button pinned to top-left of header */}
                        <div className="absolute left-4 top-6">
                            <Button
                                onClick={() => setIsSystemDetailsOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                variant="outline"
                            >
                                <ArrowLeft className="h-5 w-5 text-gray-500" />
                            </Button>
                        </div>

                        {/* Title offset so it doesn't overlap the back button */}
                        <div className="pl-14">
                            <DialogTitle>System Details</DialogTitle>
                            <DialogDescription />
                        </div>
                    </DialogHeader>

                    {!latestAlarmDetails ? (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                            No system details available yet (waiting for `alarmDetails` in the API response).
                        </div>
                    ) : (
                        <div className="space-y-6 max-h-[70vh] overflow-auto pr-1">
                            <div className="space-y-3">
                                <div className="text-sm font-semibold text-gray-900">Actuation Data</div>
                                <KeyValueTable data={latestAlarmDetails.actuationData} />
                            </div>

                            <div className="space-y-3">
                                <div className="text-sm font-semibold text-gray-900">Main Motor Data</div>
                                <KeyValueTable data={latestAlarmDetails.mainMotorData} />
                            </div>

                            <div className="space-y-3">
                                <div className="text-sm font-semibold text-gray-900">Spray Time Data</div>
                                <KeyValueTable data={latestAlarmDetails.sprayTimeData} />
                            </div>

                            <div className="space-y-3">
                                <div className="text-sm font-semibold text-gray-900">Internal Motor Data</div>
                                <KeyValueTable data={latestAlarmDetails.internalMotorData} />
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

